import type {
  CellData,
  Column,
  ReactTable,
  Row,
  RowData,
  TableFeatures
} from '@tanstack/react-table';
import type { RhinoTableFeatures } from './features';
import type { ReactNode } from 'react';
import type defaultTheme from 'tailwindcss/defaultTheme';

// Tailwind's default screens (sm–2xl) — HeroUI v3 defines no breakpoint scale
// of its own, so these are the only breakpoints in play
export type ColumnBreakpoint = keyof (typeof defaultTheme)['screens'];

// Per-column display config, carried on the TanStack column meta
export interface ColumnDisplayConfig {
  // Relative width weight. When any column in the table sets a width, the
  // table switches to fixed layout and each column gets
  // weight / totalWeight (unset columns weigh 1), so widths no longer shift
  // with the data.
  width?: number;
  // Hide this column (header and cells) below the given breakpoint
  hideBelow?: ColumnBreakpoint;
}

declare module '@tanstack/react-table' {
  interface TableMeta<
    in out TFeatures extends TableFeatures,
    in out TData extends RowData
  > {
    getRowProps?: (row: Row<TFeatures, TData>) => Record<string, unknown>;
  }
  interface ColumnMeta<
    in out TFeatures extends TableFeatures,
    in out TData extends RowData,
    TValue extends CellData = CellData
  > extends ColumnDisplayConfig {}
}

import {
  Table as NTable,
  TableContent,
  TableBody,
  TableCell,
  TableColumn,
  TableFooter,
  TableHeader,
  TableRow,
  TableScrollContainer,
  cn
} from '@heroui/react';
import { Icon } from '@iconify/react';

function SortableColumnHeader({
  children,
  sortDirection
}: {
  children: ReactNode;
  sortDirection?: 'ascending' | 'descending';
}) {
  return (
    <span className="flex items-center justify-between">
      {children}
      {!!sortDirection && (
        <Icon
          icon="gravity-ui:chevron-up"
          className={cn(
            'size-3 transform transition-transform duration-100 ease-out',
            sortDirection === 'descending' ? 'rotate-180' : ''
          )}
        />
      )}
    </span>
  );
}

// Full literal class strings so the Tailwind scanner picks them up
const HIDE_BELOW_CLASSES: Record<ColumnBreakpoint, string> = {
  sm: 'max-sm:hidden',
  md: 'max-md:hidden',
  lg: 'max-lg:hidden',
  xl: 'max-xl:hidden',
  '2xl': 'max-2xl:hidden'
};

const hideBelowClass = <TData extends RowData>(
  column: Column<RhinoTableFeatures, TData, unknown>
) => {
  const hideBelow = column.columnDef.meta?.hideBelow;
  return hideBelow ? HIDE_BELOW_CLASSES[hideBelow] : undefined;
};

interface TableProps<TData extends RowData> {
  table: ReactTable<RhinoTableFeatures, TData>;
  ariaLabel?: string;
  contentClassName?: string;
  footer?: ReactNode;
}

export const Table = <TData extends RowData>({
  table,
  ariaLabel = 'Table',
  contentClassName,
  footer
}: TableProps<TData>) => {
  const leafColumns = table.getAllLeafColumns();
  const hasWidths = leafColumns.some(
    (column) => column.columnDef.meta?.width != null
  );
  const totalWeight = leafColumns.reduce(
    (sum, column) => sum + (column.columnDef.meta?.width ?? 1),
    0
  );
  const widthStyle = (column: Column<RhinoTableFeatures, TData, unknown>) =>
    hasWidths
      ? {
          width: `${((column.columnDef.meta?.width ?? 1) / totalWeight) * 100}%`
        }
      : undefined;

  const sorting = table.state.sorting;
  const sortDescriptor =
    sorting.length > 0
      ? {
          column: sorting[0].id,
          direction: sorting[0].desc
            ? ('descending' as const)
            : ('ascending' as const)
        }
      : undefined;

  return (
    <NTable>
      <TableScrollContainer>
        <TableContent
          aria-label={ariaLabel}
          // Fixed layout is what makes the percentage widths authoritative —
          // without it the browser re-derives widths from cell content
          className={cn(hasWidths && 'table-fixed', contentClassName)}
          sortDescriptor={sortDescriptor}
          onSortChange={(descriptor) => {
            const column = table.getColumn(descriptor.column as string);
            if (column) {
              column.toggleSorting(descriptor.direction === 'descending');
            }
          }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeader key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <TableColumn
                  key={header.id}
                  id={header.id}
                  isRowHeader={index === 0}
                  allowsSorting={header.column.getCanSort()}
                  className={hideBelowClass(header.column)}
                  style={widthStyle(header.column)}
                >
                  {({ sortDirection }) => (
                    <SortableColumnHeader sortDirection={sortDirection}>
                      {header.isPlaceholder ? null : (
                        <table.FlexRender header={header} />
                      )}
                    </SortableColumnHeader>
                  )}
                </TableColumn>
              ))}
            </TableHeader>
          ))}
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const rowProps = table.options?.meta?.getRowProps?.(row) as
                { href?: string } | undefined;

              return (
                <TableRow
                  key={row.id}
                  id={row.id}
                  className={rowProps?.href ? 'cursor-pointer' : undefined}
                  {...rowProps}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={hideBelowClass(cell.column)}
                    >
                      <table.FlexRender cell={cell} />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </TableContent>
      </TableScrollContainer>
      {footer && <TableFooter>{footer}</TableFooter>}
    </NTable>
  );
};
